import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useQueryClient } from '@tanstack/react-query';
import { cn } from '@/utils/classnames';
import { useHealth, useJwks } from '@/api/hooks';
import { useIdentity } from '@/context/IdentityContext';
import { MaterialIcon } from '@/components/MaterialIcon';
import { StatusBadge } from '@/components/StatusBadge';

export interface TopBarProps {
  /** Tablet/mobile menu trigger. Optional — only rendered when provided. */
  onToggleSidebar?: () => void;
}

const ENV_LABEL = '本地 Compose';

export function TopBar({ onToggleSidebar }: TopBarProps) {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { data: health } = useHealth();
  const { data: jwks, isPending: jwksPending } = useJwks();
  const { isPlatformAdmin, principalDisplayName } = useIdentity();

  const issuer = health?.issuer ?? '—';
  const initial = (principalDisplayName ?? 'P').slice(0, 1).toUpperCase();
  const jwksVariant = jwksPending
    ? 'neutral'
    : jwks && jwks.keys.length > 0
      ? 'success'
      : 'warning';
  const jwksLabel = jwksPending
    ? 'JWKS 加载中'
    : jwks && jwks.keys.length > 0
      ? 'RS256 KEY LOADED'
      : 'JWKS 为空';

  function onReturnToLogin() {
    queryClient.clear();
    navigate('/login', { replace: true });
  }

  return (
    <header
      className={cn(
        'sticky top-0 z-30 flex h-12 items-center justify-between gap-stack-md',
        'border-b border-outline-variant bg-surface-container-lowest px-container-padding',
      )}
    >
      <div className="flex min-w-0 items-center gap-stack-md">
        {onToggleSidebar ? (
          <button
            type="button"
            onClick={onToggleSidebar}
            className="flex h-8 w-8 items-center justify-center rounded text-on-surface-variant hover:bg-surface-container-high lg:hidden"
            aria-label="切换侧边栏"
          >
            <MaterialIcon name="menu" size={20} />
          </button>
        ) : null}
        <span className="hidden font-headline-md text-headline-md text-on-surface md:inline">
          Denox Identity Fabric
        </span>
        <span className="font-label-caps text-label-caps uppercase rounded bg-surface-container px-stack-sm py-unit text-on-surface">
          {ENV_LABEL}
        </span>
        <motion.span
          layoutId="issuer-pill"
          transition={{ type: 'spring', stiffness: 380, damping: 32 }}
          className="hidden min-w-0 truncate font-code-md text-code-md text-primary md:inline"
          title={issuer}
        >
          {issuer}
        </motion.span>
        <StatusBadge variant={jwksVariant} dot>
          {jwksLabel}
        </StatusBadge>
      </div>

      <div className="flex items-center gap-stack-md">
        {isPlatformAdmin ? (
          <StatusBadge variant="info" dot>
            platform_admin
          </StatusBadge>
        ) : null}
        <div
          className={cn(
            'flex h-8 w-8 items-center justify-center rounded-full',
            'bg-primary-container text-on-primary font-label-caps text-label-caps',
          )}
          aria-label="当前用户头像"
          title={principalDisplayName ?? '后端尚未提供 /me 接口'}
        >
          {initial}
        </div>
        <button
          type="button"
          onClick={onReturnToLogin}
          className={cn(
            'inline-flex items-center gap-unit rounded px-stack-sm py-unit',
            'font-label-caps text-label-caps uppercase',
            'text-primary hover:bg-surface-container-high transition-colors duration-150',
          )}
        >
          <MaterialIcon name="logout" size={14} />
          返回登录页
        </button>
      </div>
    </header>
  );
}
